/**
 * Centralized Axios API Client
 * 
 * Provides a configured axios instance with:
 * - Request/Response interceptors
 * - Automatic token injection
 * - Centralized error handling
 * - Request/Response logging (dev mode)
 * - Retry logic for failed requests
 */

import axios from 'axios';
import { getNodeServerUrl } from '../../utils/getNodeUrl';
import { store } from '../../Redux/store';
import { API_TIMEOUT, DEFAULT_HEADERS, SALESFORCE_BASE_URL, PCMS_BASE_URL } from '../constants/api.constants';
import { getAccessToken } from '../utils/authToken';
import { formatApiError, logError } from '../utils/errorHandler';

// Create axios instances for different base URLs
const createApiClient = (baseURL, config = {}) => {
  const client = axios.create({
    baseURL,
    timeout: API_TIMEOUT,
    headers: {
      ...DEFAULT_HEADERS,
      'Access-Control-Allow-Origin': '*',
    },
    ...config,
  });

  // Request Interceptor
  client.interceptors.request.use(
    (config) => {
      // Add auth token from Redux store if available
      const state = store.getState();
      const token = state.tokens?.accessToken || getAccessToken();
      
      if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Log request in development
      if (process.env.NODE_ENV === 'development') {
        console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`, {
          data: config.data,
          params: config.params,
        });
      }

      return config;
    },
    (error) => {
      logError('API Request', error);
      return Promise.reject(error);
    }
  );

  // Response Interceptor
  client.interceptors.response.use(
    (response) => {
      // Log response in development
      if (process.env.NODE_ENV === 'development') {
        console.log(`✅ API Response: ${response.config.method?.toUpperCase()} ${response.config.url}`, {
          status: response.status,
          data: response.data,
        });
      }

      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      // Log error in development
      if (process.env.NODE_ENV === 'development') {
        console.error(`❌ API Error: ${originalRequest?.method?.toUpperCase()} ${originalRequest?.url}`, {
          status: error.response?.status,
          data: error.response?.data,
          message: error.message,
        });
      }

      // 401 handling is owned by the global apiInterceptor (services/apiInterceptor.js).
      // Do NOT attempt refresh here — just standardize the error and let it propagate.

      // Standardize error response using centralized error handler
      const standardError = formatApiError(error);
      return Promise.reject(standardError);
    }
  );

  return client;
};

// Create API clients for different services
export const nodeApi = createApiClient(getNodeServerUrl());

export const salesforceApi = createApiClient(SALESFORCE_BASE_URL);

export const pcmsApi = createApiClient(PCMS_BASE_URL);

// Helper function for API calls with standardized error handling
export const apiRequest = async (apiCall) => {
  try {
    const response = await apiCall;
    return {
      success: true,
      data: response.data,
      status: response.status,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Something went wrong',
      code: error.code,
      status: error.status,
      details: error.details,
    };
  }
};

// Export default as nodeApi for backward compatibility
export default nodeApi;

